import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import type { ErrorPolicy } from 'apollo-client';

import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop';
import { expValEquals } from '@confluence/feature-experiments';
import { useExperimentalPromotedSpacesQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalPromotedSpacesQuery';

import { SpaceEnabledContentTypesQuery } from '../queries/SpaceEnabledContentTypesQuery.graphql';
import type {
	SpaceEnabledContentTypesQuery as SpaceEnabledContentTypesQueryType,
	SpaceEnabledContentTypesQueryVariables,
} from '../queries/__types__/SpaceEnabledContentTypesQuery';
// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { GetDefaultSpaceQuery } from '../queries/GetDefaultSpaceQuery.experimentalgraphql';
import type {
	GetDefaultSpaceQuery as GetDefaultSpaceQueryType,
	GetDefaultSpaceQueryVariables,
} from '../queries/__types__/GetDefaultSpaceQuery';

export const useSpaceEnabledContentTypes = (spaceKey: string | undefined) => {
	const defaultSpaceQueryArgs = {
		skip: spaceKey !== undefined,
		variables: {
			promotedSpacesLimit: 1,
			otherSpacesLimit: 1,
			promotedSpaceKey: null,
		},
	};
	const { data: defaultSpaceKeyData } = expValEquals(
		'confluence_deprecate_experimental_promoted_spaces',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useExperimentalPromotedSpacesQuery(defaultSpaceQueryArgs)
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useQuery<GetDefaultSpaceQueryType, GetDefaultSpaceQueryVariables>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
				GetDefaultSpaceQuery,
				defaultSpaceQueryArgs,
			);

	const spaceEnabledContentTypesQueryOptions = {
		errorPolicy: 'all' as ErrorPolicy,
		skip: !spaceKey && !defaultSpaceKeyData,
		variables: {
			spaceKey: spaceKey || defaultSpaceKeyData?.experimentalPromotedSpaces?.nodes?.[0]?.id,
		},
	};
	const { data: enabledContentTypesData } = useQueryNoFurtherUpdateInterop<
		SpaceEnabledContentTypesQueryType,
		SpaceEnabledContentTypesQueryVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		SpaceEnabledContentTypesQuery,
		spaceEnabledContentTypesQueryOptions,
	);

	const isCreateWhiteboardEnabled = Boolean(
		enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isWhiteboardsEnabled,
	);

	const isCreateBlogEnabled = Boolean(
		enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isBlogsEnabled,
	);

	const isCreateDatabaseEnabled = Boolean(
		enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isDatabasesEnabled,
	);

	const isCreateSmartLinkEnabled = Boolean(
		enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isEmbedsEnabled,
	);

	const isCreateLiveDocEnabled = Boolean(
		enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isLivePagesEnabled,
	);

	const isCreateFolderEnabled = Boolean(
		enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isFoldersEnabled,
	);

	return {
		isCreateWhiteboardEnabled,
		isCreateBlogEnabled,
		isCreateDatabaseEnabled,
		isCreateSmartLinkEnabled,
		isCreateLiveDocEnabled,
		isCreateFolderEnabled,
	};
};
